import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, getAdminApiUrl } from '../lib/supabase';
import Logo from './Logo';

type FlagStatus = 'pending' | 'reviewed' | 'resolved' | 'dismissed' | 'moderated';

interface InappropriateFlag {
  id: string;
  listing_id: string;
  listing_type: string;
  listing_user_id: string;
  reporter_user_id: string;
  flag_category: string;
  flag_reason: string | null;
  status: FlagStatus;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  action_taken: string | null;
  action_notes: string | null;
  created_at: string;
  updated_at: string;
}

// Add: Pencil SVG for notes
const NoteIcon = ({className = "w-5 h-5"}) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-2.036A2.5 2.5 0 1117.5 8.25L6.75 19.5H3v-3.75L15.232 5.232z" />
  </svg>
);

const InappropriateFlags: React.FC = () => {
  const navigate = useNavigate();
  const [flags, setFlags] = useState<InappropriateFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null); // tracks expanded accordian
  const [details, setDetails] = useState<Record<string, any>>({}); // {flag id: listing details}
  const [detailsLoading, setDetailsLoading] = useState<string | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [statusLoadingForFlagId, setStatusLoadingForFlagId] = useState<string | null>(null);
  const [flagStatusLoadingForId, setFlagStatusLoadingForId] = useState<string | null>(null); // tracks which flag status is being updated
  const [noteEditForId, setNoteEditForId] = useState<string | null>(null); // tracks which listing row is open for notes (by listing_id)
  const [noteText, setNoteText] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [notesByListing, setNotesByListing] = useState<Record<string, any[]>>({}); // key by listing_id for UI grouping
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<FlagStatus | 'all' | null>(null);
  const itemsPerPage = 50;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No active session');
        }
        const res = await fetch(getAdminApiUrl(), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'listInappropriateFlags' }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to load flags');
        }
        const payload = await res.json();
        setFlags(payload.flags as InappropriateFlag[]);
      } catch (e: any) {
        setError(e.message || 'Failed to load flags');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredFlags = flags.filter(f => {
    // Filter by search term
    const matchesSearch = !search.trim() || (() => {
      const hay = [
        f.flag_category,
        f.flag_reason || '',
        f.status || '',
      ].join(' ').toLowerCase();
      return hay.includes(search.toLowerCase());
    })();
    
    // Filter by status if a status filter is selected
    const matchesStatus = !selectedStatusFilter || selectedStatusFilter === 'all' || f.status === selectedStatusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Reset to page 1 when search or status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedStatusFilter]);

  // Calculate counts for each status
  const statusCounts = {
    all: flags.length,
    pending: flags.filter(f => f.status === 'pending').length,
    reviewed: flags.filter(f => f.status === 'reviewed').length,
    resolved: flags.filter(f => f.status === 'resolved').length,
    dismissed: flags.filter(f => f.status === 'dismissed').length,
    moderated: flags.filter(f => f.status === 'moderated').length,
  };

  const handleStatusFilterClick = (status: FlagStatus | 'all') => {
    setSelectedStatusFilter(status === selectedStatusFilter ? null : status);
    setSearch(''); // Clear search when filter is clicked
  };

  const clearStatusFilter = () => {
    setSelectedStatusFilter(null);
  };

  const formatDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleString() : '—';

  const handleAccordionToggle = async (flag: InappropriateFlag) => {
    if (expandedId === flag.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(flag.id);
    if (!details[flag.id]) {
      setDetailsLoading(flag.id);
      setDetailsError(null);
      try {
        let detail: any = null;
        if (flag.listing_type === 'product') {
          const { data, error } = await supabase.from('ProductListings').select('*').eq('id', flag.listing_id).single();
          if (error) throw error;
          detail = data;
        } else if (flag.listing_type === 'jobrequests') {
          const { data, error } = await supabase.from('jobrequests').select('*').eq('id', flag.listing_id).single();
          if (error) throw error;
          detail = data;
        } else if (flag.listing_type === 'rental') {
          const { data, error } = await supabase.from('rental_listings').select('*').eq('id', flag.listing_id).single();
          if (error) throw error;
          detail = data;
        }
        setDetails(prev => ({ ...prev, [flag.id]: detail }));
      } catch (err: any) {
        setDetailsError('Error loading listing details');
      } finally {
        setDetailsLoading(null);
      }
    }
  };

  const handleUpdateListingStatus = async (flag: InappropriateFlag) => {
    try {
      setStatusLoadingForFlagId(flag.id);
      let table = '';
      if (flag.listing_type === 'product') table = 'ProductListings';
      else if (flag.listing_type === 'jobrequests') table = 'jobrequests';
      else if (flag.listing_type === 'rental') table = 'rental_listings';
      else throw new Error('Unknown listing type');

      // Get current status from details or fetch it
      let currentStatus = details[flag.id]?.status;
      
      // If details not loaded, fetch current status
      if (!currentStatus) {
        const { data: currentDetail, error: fetchError } = await supabase
          .from(table)
          .select('status')
          .eq('id', flag.listing_id)
          .single();
        
        if (fetchError) throw fetchError;
        currentStatus = currentDetail?.status;
      }

      // Toggle status: if 'moderated', change to 'active', otherwise change to 'moderated'
      const newStatus = currentStatus === 'moderated' ? 'active' : 'moderated';

      const { error } = await supabase
        .from(table)
        .update({ status: newStatus })
        .eq('id', flag.listing_id);
      if (error) throw error;

      // Optimistically update the loaded details if present
      if (details[flag.id]) {
        setDetails(prev => ({ ...prev, [flag.id]: { ...prev[flag.id], status: newStatus } }));
      }
    } catch (e) {
      // noop UI toast could be added
    } finally {
      setStatusLoadingForFlagId(null);
    }
  };

  const handleFlagStatusChange = async (flagId: string, newStatus: FlagStatus) => {
    try {
      setFlagStatusLoadingForId(flagId);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !user.id) {
        throw new Error('No active admin session!');
      }

      // Update the flag status in inappropriate_content_flags table
      const { error } = await supabase
        .from('inappropriate_content_flags')
        .update({ 
          status: newStatus,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', flagId);

      if (error) throw error;

      // Update local state
      setFlags(prevFlags => 
        prevFlags.map(flag => 
          flag.id === flagId 
            ? { ...flag, status: newStatus, reviewed_by: user.id, reviewed_at: new Date().toISOString() }
            : flag
        )
      );
    } catch (err: any) {
      console.error('Error updating flag status:', err);
      // Could add toast notification here
    } finally {
      setFlagStatusLoadingForId(null);
    }
  };

  const handleNoteIconClick = async (listingId: string, listingUserId: string, listingType: string) => {
    setNoteError(null);
    setNoteEditForId(listingId);
    setNoteLoading(true);
    setNoteText("");
    try {
      // Fetch notes scoped to this listing (listing_id + listing_type)
      const { data, error } = await supabase
        .from('admin_notes')
        .select('*')
        .eq('listing_id', listingId)
        .eq('listing_type', listingType)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setNotesByListing(prev => ({ ...prev, [listingId]: data || [] }));
    } catch (err: any) {
      setNoteError("Failed to load admin notes.");
    } finally {
      setNoteLoading(false);
    }
  };

  const handleNoteSave = async (listingId: string, listingUserId: string, listingType: string) => {
    setNoteError(null);
    setNoteLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !user.id) {
        setNoteError('No active admin session!');
        setNoteLoading(false);
        return;
      }
      if (!noteText.trim()) {
        setNoteError('Please enter some text.');
        setNoteLoading(false);
        return;
      }
      // Insert a NEW note scoped to this listing and tied to the listing owner
      const { error } = await supabase.from('admin_notes').insert([
        {
          user_id: listingUserId,
          admin_id: user.id,
          note_content: noteText.trim(),
          note_type: 'flag-listing',
          listing_id: listingId,
          listing_type: listingType,
        },
      ]);
      if (error) throw error;
      setNoteText(""); // clear input
      // refetch notes after adding
      await handleNoteIconClick(listingId, listingUserId, listingType);
    } catch (err: any) {
      setNoteError('Failed to save note.');
    } finally {
      setNoteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#00a67e] shadow-sm border-b border-[#008f6b]">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Logo size={36} />
              <div>
                <h1 className="text-2xl font-bold text-white">Inappropriate Content Flags</h1>
                <p className="text-green-100 text-sm">Review reports</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-[#008f6b] text-white rounded-lg hover:bg-[#007a5a] transition-colors duration-200"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : error ? (
          <div className="rounded-md bg-red-50 p-4 border border-red-200">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        ) : (() => {
          // Pagination calculations
          const totalPages = Math.ceil(filteredFlags.length / itemsPerPage);
          const startIndex = (currentPage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          const paginatedFlags = filteredFlags.slice(startIndex, endIndex);
          
          return (
            <>
              {/* Status Filter Tiles */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                {/* All Flags Tile */}
                <div
                  className={`bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                    selectedStatusFilter === 'all' ? 'ring-2 ring-blue-400 shadow-lg' : ''
                  }`}
                  onClick={() => handleStatusFilterClick('all')}
                  title="Click to show all flags"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-blue-600">All Flags</p>
                      <p className="text-2xl font-bold text-blue-900">{statusCounts.all}</p>
                    </div>
                  </div>
                </div>

                {/* Pending Tile */}
                <div
                  className={`bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg p-4 border border-yellow-100 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                    selectedStatusFilter === 'pending' ? 'ring-2 ring-yellow-400 shadow-lg' : ''
                  }`}
                  onClick={() => handleStatusFilterClick('pending')}
                  title="Click to show pending flags"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-yellow-600">Pending</p>
                      <p className="text-2xl font-bold text-yellow-900">{statusCounts.pending}</p>
                    </div>
                  </div>
                </div>

                {/* Reviewed Tile */}
                <div
                  className={`bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-100 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                    selectedStatusFilter === 'reviewed' ? 'ring-2 ring-purple-400 shadow-lg' : ''
                  }`}
                  onClick={() => handleStatusFilterClick('reviewed')}
                  title="Click to show reviewed flags"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-purple-500 rounded-lg flex items-center justify-center">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-purple-600">Reviewed</p>
                      <p className="text-2xl font-bold text-purple-900">{statusCounts.reviewed}</p>
                    </div>
                  </div>
                </div>

                {/* Resolved Tile */}
                <div
                  className={`bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                    selectedStatusFilter === 'resolved' ? 'ring-2 ring-green-400 shadow-lg' : ''
                  }`}
                  onClick={() => handleStatusFilterClick('resolved')}
                  title="Click to show resolved flags"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-green-600">Resolved</p>
                      <p className="text-2xl font-bold text-green-900">{statusCounts.resolved}</p>
                    </div>
                  </div>
                </div>

                {/* Dismissed Tile */}
                <div
                  className={`bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg p-4 border border-gray-100 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                    selectedStatusFilter === 'dismissed' ? 'ring-2 ring-gray-400 shadow-lg' : ''
                  }`}
                  onClick={() => handleStatusFilterClick('dismissed')}
                  title="Click to show dismissed flags"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-gray-500 rounded-lg flex items-center justify-center">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-gray-600">Dismissed</p>
                      <p className="text-2xl font-bold text-gray-900">{statusCounts.dismissed}</p>
                    </div>
                  </div>
                </div>

                {/* Moderated Tile */}
                <div
                  className={`bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-4 border border-orange-100 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                    selectedStatusFilter === 'moderated' ? 'ring-2 ring-orange-400 shadow-lg' : ''
                  }`}
                  onClick={() => handleStatusFilterClick('moderated')}
                  title="Click to show moderated flags"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-orange-500 rounded-lg flex items-center justify-center">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-orange-600">Moderated</p>
                      <p className="text-2xl font-bold text-orange-900">{statusCounts.moderated}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Filter Display */}
              {selectedStatusFilter && (
                <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                        </svg>
                        <span className="font-medium text-amber-900">Active Filter:</span>
                        <span className="ml-2 text-amber-800 font-mono bg-amber-100 px-2 py-1 rounded capitalize">
                          {selectedStatusFilter === 'all' ? 'All Flags' : selectedStatusFilter}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={clearStatusFilter}
                      className="flex items-center px-3 py-2 text-sm font-medium text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Clear Filter
                    </button>
                  </div>
                </div>
              )}

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flag Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flag Reason</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flagged User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporter</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Listing User ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Listing ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                        <th className="px-4 py-3"></th>{/* Chevron */}
                        <th className="px-2 py-3"></th>{/* Notes Icon */}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedFlags.map((f) => (
                    <React.Fragment key={f.id}>
                      <tr className="align-top group hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">
                          <div className="font-medium">{f.flag_category.replace(/_/g, ' ')}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">
                          {f.flag_reason || <span className="text-gray-500">N/A</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">
                          <select
                            value={f.status}
                            onChange={(e) => handleFlagStatusChange(f.id, e.target.value as FlagStatus)}
                            disabled={flagStatusLoadingForId === f.id}
                            className={`px-2 py-1 text-xs border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                              flagStatusLoadingForId === f.id
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-900 cursor-pointer hover:bg-gray-50'
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="resolved">Resolved</option>
                            <option value="dismissed">Dismissed</option>
                            <option value="moderated">Moderated</option>
                          </select>
                          {flagStatusLoadingForId === f.id && (
                            <span className="ml-2 text-xs text-gray-500">Updating...</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">{f.listing_user_id}</td>
                        <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">{f.reporter_user_id}</td>
                        <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">{f.listing_user_id}</td>
                        <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">{f.listing_id}</td>
                        <td className="px-6 py-4 whitespace-normal text-xs text-gray-500">{formatDate(f.created_at)}</td>
                        <td className="px-4 py-4 w-10">
                          <button
                            onClick={() => handleAccordionToggle(f)}
                            aria-label="Show details"
                            className="flex items-center justify-center cursor-pointer rounded-lg p-2 bg-[#e6f7f0] hover:bg-[#ccf0e0] transition-colors duration-200"
                          >
                            {/* Chevron Icon SVG */}
                            <svg
                              className={`w-4 h-4 text-[#00a67e] transition-transform duration-200 ${expandedId === f.id ? 'rotate-180' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </td>
                        <td className="px-2 py-4 w-10">
                          <button
                            onClick={() => handleNoteIconClick(f.listing_id, f.listing_user_id, f.listing_type)}
                            className="flex items-center justify-center cursor-pointer rounded-lg p-2 bg-yellow-50 hover:bg-yellow-100 transition-colors duration-200"
                            aria-label="Edit/View Admin Notes"
                          >
                            <NoteIcon />
                          </button>
                        </td>
                      </tr>
                      {expandedId === f.id && (
                        <tr>
                          <td colSpan={10} className="p-0 bg-green-50">
                            <div className="p-4 border-t border-green-200">
                              {detailsLoading === f.id ? (
                                <div className="flex"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div> <span className="ml-2">Loading details...</span></div>
                              ) : detailsError ? (
                                <div className="text-red-500">{detailsError}</div>
                              ) : (
                                <AccordionContent
                                  flag={f}
                                  detail={details[f.id]}
                                  onUpdateStatus={() => handleUpdateListingStatus(f)}
                                  updating={statusLoadingForFlagId === f.id}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      {noteEditForId === f.listing_id && (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <div className="bg-white p-4 border-t border-yellow-300">
                              {/* Notes history, if any */}
                              {notesByListing[f.listing_id]?.length > 0 && (
                                <div className="mb-4 space-y-3">
                                  {notesByListing[f.listing_id].map((n) => (
                                    <div key={n.id} className="border-b pb-2">
                                      <div className="flex justify-between items-center text-xs text-gray-500 mb-0.5">
                                        <div className="flex items-center gap-2">
                                          <span>{n.admin_email || n.admin_id || 'Admin'}</span>
                                          {n.note_type && (
                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                              {n.note_type}
                                            </span>
                                          )}
                                        </div>
                                        <span>{new Date(n.created_at).toLocaleString()}{n.updated_at !== n.created_at && <span className="ml-2 text-gray-400">(edited)</span>}</span>
                                      </div>
                                      <div className="text-gray-900 mb-2 whitespace-pre-wrap text-sm">{n.note_content}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <form
                                className="flex flex-col gap-3"
                                onSubmit={e => {
                                  e.preventDefault();
                                  handleNoteSave(f.listing_id, f.listing_user_id, f.listing_type);
                                }}
                              >
                                <div className="font-medium text-yellow-900">Admin Note for Listing</div>
                                <textarea
                                  className="border p-2 rounded w-full text-sm"
                                  rows={3}
                                  value={noteText}
                                  disabled={noteLoading}
                                  onChange={e => setNoteText(e.target.value)}
                                  placeholder="Add admin notes or context for this listing..."
                                />
                                {noteError && <div className="text-xs text-red-600 mt-2">{noteError}</div>}
                                <div className="flex gap-2 mt-1">
                                  <button
                                    type="submit"
                                    disabled={noteLoading}
                                    className="bg-yellow-500 hover:bg-yellow-600 text-white rounded px-3 py-1 text-xs font-semibold"
                                  >{noteLoading ? 'Saving...' : 'Save'}</button>
                                  <button
                                    type="button"
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded px-3 py-1 text-xs font-semibold"
                                    disabled={noteLoading}
                                    onClick={() => setNoteEditForId(null)}
                                  >Cancel</button>
                                </div>
                              </form>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Pagination Controls */}
              {filteredFlags.length > 0 && totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(endIndex, filteredFlags.length)}</span> of{' '}
                        <span className="font-medium">{filteredFlags.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        {[...Array(totalPages)].map((_, i) => {
                          const pageNum = i + 1;
                          // Show first page, last page, current page, and pages around current
                          if (
                            pageNum === 1 ||
                            pageNum === totalPages ||
                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  currentPage === pageNum
                                    ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          } else if (
                            pageNum === currentPage - 2 ||
                            pageNum === currentPage + 2
                          ) {
                            return (
                              <span
                                key={pageNum}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                              >
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}
                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === totalPages
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

// AccordionContent component -- customize per listing type
const AccordionContent: React.FC<{ flag: InappropriateFlag, detail: any, onUpdateStatus: () => void, updating: boolean }> = ({ flag, detail, onUpdateStatus, updating }) => {
  const truncateUrl = (url: string, max = 36) =>
    url.length <= max ? url : `${url.slice(0, 16)}...${url.slice(-16)}`;
  
  if (!detail) return <div className="text-gray-500">No details found.</div>;
  if (flag.listing_type === 'product') {
    return (
      <div>
        <div className="font-semibold">Product Listing Details</div>
        <div className="text-sm mt-1">ID: {detail.id}</div>
        <div className="text-sm mt-1">Title: {detail.title}</div>
        <div className="text-sm mt-1">Description: {detail.description}</div>
        <div className="text-sm mt-1 flex items-center gap-2">
          <span>Status: {detail.status ?? 'N/A'}</span>
          <button
            className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            onClick={onUpdateStatus}
            disabled={updating}
          >{updating ? 'Updating…' : detail.status === 'moderated' ? 'Set to Active' : 'Set to Moderated'}</button>
        </div>
        <div className="text-sm mt-1">Seller ID: {detail.seller_id}</div>
        {/* Images */}
        <div className="flex flex-wrap gap-4 mt-2">
          {detail.images && detail.images.length > 0 && (
            detail.images.map((img: string, idx: number) => (
              <div key={idx} className="flex flex-col items-center">
                <img src={img} alt="product" className="w-20 h-20 object-cover rounded mb-0.5" />
                <a
                  href={img}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs text-blue-700 underline break-all max-w-[120px]"
                  style={{ wordBreak: 'break-all' }}
                  title={img}
                >
                  {truncateUrl(img)}
                </a>
              </div>
            ))
          )}
        </div>
        {/* Video/Video URLs */}
        {detail.video && (typeof detail.video === 'string' ? (
          <video src={detail.video} controls className="mt-2 w-40" />
        ) : detail.video_urls && detail.video_urls.length > 0 && (
          <video src={detail.video_urls[0]} controls className="mt-2 w-40" />
        ))}
      </div>
    );
  } else if (flag.listing_type === 'jobrequests') {
    return (
      <div>
        <div className="font-semibold">Job Request Details</div>
        <div className="text-sm mt-1">Title: {detail.title}</div>
        <div className="text-sm mt-1">Description: {detail.description}</div>
        <div className="text-sm mt-1 flex items-center gap-2">
          <span>Status: {detail.status ?? 'N/A'}</span>
          <button
            className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            onClick={onUpdateStatus}
            disabled={updating}
          >{updating ? 'Updating…' : detail.status === 'moderated' ? 'Set to Active' : 'Set to Moderated'}</button>
        </div>
        <div className="flex flex-wrap gap-4 mt-2">
          {detail.photos && detail.photos.length > 0 && (
            detail.photos.map((img: string, idx: number) => (
              <div key={idx} className="flex flex-col items-center">
                <img src={img} alt="job" className="w-20 h-20 object-cover rounded mb-0.5" />
                <a
                  href={img}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs text-blue-700 underline break-all max-w-[120px]"
                  style={{ wordBreak: 'break-all' }}
                  title={img}
                >
                  {truncateUrl(img)}
                </a>
              </div>
            ))
          )}
        </div>
      </div>
    );
  } else if (flag.listing_type === 'rental') {
    return (
      <div>
        <div className="font-semibold">Rental Listing Details</div>
        <div className="text-sm mt-1">Title: {detail.title}</div>
        <div className="text-sm mt-1">Description: {detail.description}</div>
        <div className="text-sm mt-1 flex items-center gap-2">
          <span>Status: {detail.status ?? 'N/A'}</span>
          <button
            className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            onClick={onUpdateStatus}
            disabled={updating}
          >{updating ? 'Updating…' : detail.status === 'moderated' ? 'Set to Active' : 'Set to Moderated'}</button>
        </div>
        <div className="flex flex-wrap gap-4 mt-2">
          {detail.images && detail.images.length > 0 && (
            detail.images.map((img: string, idx: number) => (
              <div key={idx} className="flex flex-col items-center">
                <img src={img} alt="rental" className="w-20 h-20 object-cover rounded mb-0.5" />
                <a
                  href={img}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs text-blue-700 underline break-all max-w-[120px]"
                  style={{ wordBreak: 'break-all' }}
                  title={img}
                >
                  {truncateUrl(img)}
                </a>
              </div>
            ))
          )}
        </div>
        {detail.video_url && (
          <video src={detail.video_url} controls className="mt-2 w-40" />
        )}
      </div>
    );
  }
  return <div className="text-gray-500">Unknown listing type.</div>;
};

export default InappropriateFlags;


